import get from 'lodash/get';
import { Success, JsonObj } from '@mhp/general-interface';
import * as actions from 'store/profile/actions';
import { AnyAction } from 'redux';
import { Action, RequestState } from 'modules/types/common';
import { createFetchReducer, setFetchingSuccessState } from 'store/setLocalStore';
import { combineReducers } from '@reduxjs/toolkit';
import { normalizeProfile } from './mappers';
import {
  Consumer,
  RequestedProxy,
  ProfileState,
  ValidateResultsState,
  ConsumerMFAStatus
} from './types';

const startState = (state: {}) => ({
  ...state,
  isFetching: true,
  error: null,
  profileRequestState: RequestState.REQUESTING
});

const startStateForInsurance = (state: {}) => ({
  ...state,
  insuranceRequestState: RequestState.REQUESTING,
  error: null
});

const failState = (state: {}, action: {}) => ({
  ...state,
  isFetching: false,
  error: get(action, 'error'),
  profileRequestState: RequestState.ERROR
});

const failStateForInsurance = (state: {}, action: {}) => ({
  ...state,
  insuranceRequestState: RequestState.ERROR,
  insError: get(action, 'error')
});

// DEPRECATING
export const initialValidationState = {
  status: null,
  type: null,
  error: null,
  isFetching: false
};

export const profileInitialState: ProfileState = {
  isHydrated: false,
  isFetching: false,
  error: null,
  insError: null,
  visitpayEnrolledAndAccessible: false,
  visitpayEnrolledRequestState: RequestState.INITIALIZED,
  insuranceRequestState: RequestState.INITIALIZED,
  lastUpdated: null,

  // Top level Profile keys
  proxyToUpdate: {} as RequestedProxy,
  customAttributes: [],
  requestedProxies: [],
  grantors: [],
  grantees: [],
  insurances: [],
  activeInsurance: null,
  preferredProviders: [],
  preferredLocations: [],
  // DEPRECATING
  validation: initialValidationState,
  consumer: {} as Consumer,
  consumerMFAStatus: {} as ConsumerMFAStatus,
  profileRequestState: RequestState.INITIALIZED
};

const grabError = (action: JsonObj, type: Error | string) => ({
  type
});

const profileReducer = (state = profileInitialState, action: AnyAction) => {
  switch (action.type) {
    case actions.RESET_PROFILE: {
      return profileInitialState;
    }
    case actions.GET_PROFILE_FOR_CONSUMER:
    case actions.GET_PROFILE: {
      return startState(state);
    }
    case actions.GET_PROFILE_SUCCESS:
    case actions.UPDATE_CONSUMER_PROFILE_SUCCESS: {
      return {
        ...state,
        ...normalizeProfile(get(action, 'payload.data')),
        lastUpdated: Date.now(),
        isFetching: false,
        isHydrated: true,
        profileRequestState: RequestState.SUCCESS
      };
    }
    case actions.GET_PROFILE_FOR_CONSUMER_SUCCESS: {
      return {
        ...state,
        isFetching: false,
        error: null,
        profileRequestState: RequestState.SUCCESS
      };
    }
    case actions.GET_PROFILE_FAIL:
      return failState(state, action);
    case actions.GET_PROFILE_FOR_INSURANCE:
    case actions.GET_PROFILE_FOR_CONSUMER_INSURANCE:
      return startStateForInsurance(state);
    case actions.GET_PROFILE_FOR_CONSUMER_INSURANCE_SUCCESS:
    case actions.GET_PROFILE_FOR_INSURANCE_SUCCESS: {
      return {
        ...state,
        insurances: get(action, 'payload.data'),
        lastUpdated: Date.now(),
        insuranceRequestState: RequestState.SUCCESS
      };
    }
    case actions.GET_PROFILE_FOR_CONSUMER_FAIL:
    case actions.GET_PROFILE_FOR_CONSUMER_INSURANCE_FAIL:
    case actions.GET_PROFILE_FOR_INSURANCE_FAIL: {
      return failStateForInsurance(state, action);
    }
    case actions.SET_ACTIVE_INSURANCE:
      return {
        ...state,
        activeInsurance: action.payload
      };
    case actions.ACCEPT_T_AND_C:
      return {
        ...state,
        termAcceptance: { version: action.payload.version, acceptanceDate: action.payload.time }
      };
    // DEPRECATING
    case actions.SEND_PIN:
    case actions.SEND_PIN_OTP:
      return {
        ...state,
        validation: { ...initialValidationState, isFetching: true }
      };
    // DEPRECATING
    case actions.RESET_VALIDATION:
    case actions.SEND_PIN_SUCCESS:
    case actions.SEND_PIN_OTP_SUCCESS:
      return {
        ...state,
        validation: initialValidationState
      };
    // DEPRECATING
    case actions.SEND_PIN_FAIL:
    case actions.SEND_PIN_OTP_FAIL:
      return {
        ...state,
        validation: {
          error: grabError(action, actions.SEND_PIN_FAIL)
        }
      };
    // DEPRECATING
    case actions.VALIDATE_PIN_SUCCESS:
      return {
        ...state,
        validation: {
          status: get(action, 'payload.data.statusCode', ''),
          error: null
        }
      };
    // DEPRECATING
    case actions.VALIDATE_PIN_FAIL:
      return {
        ...state,
        validation: {
          error: grabError(action, actions.VALIDATE_PIN_FAIL)
        }
      };
    case actions.ADD_REQUESTED_PROXIES:
      return {
        ...state,
        requestedProxies: [...state.requestedProxies, ...action.payload.data]
      };
    case actions.CLEAR_REQUESTED_PROXIES:
      return {
        ...state,
        requestedProxies: [],
        proxyToUpdate: {} as RequestedProxy
      };
    case actions.DELETE_REQUESTED_PROXY:
      return {
        ...state,
        requestedProxies: [...action.payload.data]
      };
    case actions.SET_PROXY_TO_UPDATE:
      return {
        ...state,
        proxyToUpdate: action.payload.data
      };
    case actions.RESET_PROXY_TO_UPDATE:
      return {
        ...state,
        proxyToUpdate: {}
      };
    case actions.UPDATE_PROXY:
      return {
        ...state,
        requestedProxies: action.payload.data,
        proxyToUpdate: {} as RequestedProxy
      };
    case actions.GET_VISITPAY_ENROLLED_INFO: {
      return {
        ...state,
        visitpayEnrolledRequestState: RequestState.REQUESTING
      };
    }
    case actions.GET_VISITPAY_ENROLLED_INFO_SUCCESS: {
      return {
        ...state,
        visitpayEnrolledRequestState: RequestState.SUCCESS,
        visitpayEnrolledAndAccessible: action.payload.data.isEnrolled
      };
    }
    case actions.GET_VISITPAY_ENROLLED_INFO_FAIL: {
      return {
        ...state,
        visitpayEnrolledRequestState: RequestState.ERROR,
        visitpayEnrolledAndAccessible: false
      };
    }
    case actions.UPDATE_CONSUMER_CONTACT_EMAIL: {
      return {
        ...state,
        consumer: {
          ...state.consumer,
          emails: [action.payload.data]
        }
      };
    }
    case actions.UPDATE_CONSUMER_CONTACT_PHONE: {
      return {
        ...state,
        consumer: {
          ...state.consumer,
          phones: [action.payload.data]
        }
      };
    }
    case actions.UPDATE_PROFILE_MFA_ENABLED_FLAG_SUCCESS: {
      let mfaEnabled = false;
      if (action.payload?.config?.data) {
        if (typeof action.payload?.config?.data === 'string') {
          const data = JSON.parse(action.payload.config.data);
          mfaEnabled = !!data.mfaEnabled;
        } else {
          mfaEnabled = !!action.payload.config.data.mfaEnabled;
        }
      } else if (action.payload?.data) {
        mfaEnabled = !!action.payload.data.success;
      }
      return {
        ...state,
        consumer: {
          ...state.consumer,
          mfaEnabled
        }
      };
    }
    case actions.UPDATE_PROFILE_MFA_ENABLED_FLAG_FAIL: {
      return {
        ...state,
        consumer: {
          ...state.consumer,
          mfaEnabled: false
        }
      };
    }

    case actions.GET_CONSUMER_MFA_STATUS_SUCCESS: {
      return {
        ...state,
        consumerMFAStatus: action.payload.data
      };
    }

    case actions.GET_CONSUMER_MFA_STATUS_FAIL: {
      return {
        ...state,
        consumerMFAStatus: {}
      };
    }
    default:
      return state;
  }
};
export default profileReducer;

export const validation = combineReducers({
  sendOtp: createFetchReducer<Success>(actions.SEND_IDW_PIN),
  validate: createFetchReducer<Success>(actions.SEND_IDW_VERIFICATION, true, {
    [actions.SEND_IDW_VERIFICATION_SUCCESS]: (state: JsonObj, action: Action<JsonObj>) => {
      setFetchingSuccessState(state as ValidateResultsState, action);
      if (state?.data?.success && state?.data?.verifyCode && state?.data?.verifySource) {
        if (!state.results) {
          state.results = {};
        }
        state.results[state?.data?.verifySource] = {
          verifyCode: state?.data?.verifyCode,
          verifySource: state?.data?.verifySource
        };
      }
    },
    [actions.RESET_VALIDATION]: (state: JsonObj, action?: { payload?: { key?: string } }) => {
      delete state.data;
      delete state.error;
      delete state.lastUpdated;
      state.isFetching = false;
      state.requestState = RequestState.INITIALIZED;
      if (state.results) {
        state.results[action?.payload?.key] = null;
      }
    }
  }),
  emailUpdate: createFetchReducer<Success>(actions.UPDATE_EMAIL),
  phoneUpdate: createFetchReducer<Success>(actions.UPDATE_PHONE)
});
